import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { Injectable } from "@angular/core";
import { LoggerService } from "@bfx/logger";
import {
  BfxConfigService,
  BfxSysConfigUtilService,
  SystemConfigPromiseService,
} from "shared/auth-services";
import {
  AlgoStrategyLookupService,
  CcyPairLookupService,
  CcyLookupService,
  ClientLookupService,
  CurrencyPairCalendarLookupService,
  ProfilePromiseActionService,
  ProfileSettingKeys,
  SalesGroupLookupService,
  TableDefinition,
  UserLookupService,
  AccountLookupService,
  IndexType,
  ColumnFilterType,
  StaticDataPrivatizationType,
} from "shared/cubiq-services";
import { FwdRollBaseTableDefinitionFactory } from "shared/cubiq-services/model/FwdRollBaseTableDefinitionFactory";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";
import { FWD_ROLL_ORDERS_TABLE_DEFINITION_VER } from "shared/cubiq-services/model/TableDefinitionFactory.constants";
import { PrivatizationHelper } from "shared/cubiq-services/model/orders/PrivatizationHelper";
import { ValueFormatterParams } from "ag-grid-community/main";
const logger = LoggerService.for(__dirname);

@Injectable()
export class FwdRollOrdersTableDefinitionFactory extends FwdRollBaseTableDefinitionFactory {
  private privatizationHelper: PrivatizationHelper;
  constructor(
    profileService: ProfilePromiseActionService,
    ccyPairLookupService: CcyPairLookupService,
    ccyLookupService: CcyLookupService,
    sysConfigService: SystemConfigPromiseService,
    clientLookup: ClientLookupService,
    accountLookup: AccountLookupService,
    userLookupService: UserLookupService,
    salesGroupLookup: SalesGroupLookupService,
    currencyPairCalendarLookupService: CurrencyPairCalendarLookupService,
    algoStrategyLookupService: AlgoStrategyLookupService,
    sysConfigUtil: BfxSysConfigUtilService,
    configService: BfxConfigService,
  ) {
    super(
      profileService,
      ccyPairLookupService,
      ccyLookupService,
      sysConfigService,
      clientLookup,
      userLookupService,
      salesGroupLookup,
      currencyPairCalendarLookupService,
      algoStrategyLookupService,
      sysConfigUtil,
      configService,
    );
    this.privatizationHelper = new PrivatizationHelper(clientLookup, accountLookup);
  }

  public override createDef(): TableDefinition {
    super.createDef();

    let td = new TableDefinition("rowId", "allocationId");

    td.setDefaultSort(1, "desc");

    // Account Name
    let statusCol: BfxColDef = this.createOrderStatusColumn();
    td.addColumn(statusCol);

    // Account Name
    let accountNmCol: BfxColDef = this.createAccountNameColumn();
    td.addColumn(accountNmCol);

    // dealt pair
    let instrumentCol: BfxColDef = this.createInstrumentColumn();
    td.addColumn(instrumentCol);

    // dealt direction
    let intentCol: BfxColDef = this.createIntentColumn();
    td.addColumn(intentCol);

    // dealt ccy
    let dealtCol: BfxColDef = this.createDealtCcyColumn();
    td.addColumn(dealtCol);

    // value date
    let valueDateCol: BfxColDef = this.createValueDateColumn();
    td.addColumn(valueDateCol);

    // Dealt Amount
    let orderAmtCol: BfxColDef = this.createDealtAmtColumn();
    td.addColumn(orderAmtCol);

    // counter Amount
    let counterAmtCol: BfxColDef = this.createCounterAmtColumn();
    td.addColumn(counterAmtCol);

    // market forward points
    let marketForwardPointsCol: BfxColDef = this.createMarketForwardPtsColumn();
    td.addColumn(marketForwardPointsCol);

    // agreed forward points
    let agreedForwardPointsCol: BfxColDef = this.createAgreedForwardPtsColumn();
    td.addColumn(agreedForwardPointsCol);

    // adjusted vs agreed forward points percent change
    let agreedPercentChangeCol: BfxColDef = this.createAgreedPercentChangeColumn();
    td.addColumn(agreedPercentChangeCol);

    // fwd markup
    let fwdMarkupCol: BfxColDef = this.createForwardMarkupColumn();
    td.addColumn(fwdMarkupCol);

    // clientPts
    let clientPtsCol: BfxColDef = this.createClientForwardPtsColumn();
    td.addColumn(clientPtsCol);

    // market spot
    let marketSpotCol: BfxColDef = this.createMarketSpotColumn(false);
    td.addColumn(marketSpotCol);

    // spot markup
    let spotMarkupCol: BfxColDef = this.createSpotMarkupColumn();
    td.addColumn(spotMarkupCol);

    // client spot
    let clientSpotCol: BfxColDef = this.createClientSpotColumn();
    td.addColumn(clientSpotCol);

    // market all-in rate
    let marketForwardRateCol: BfxColDef = this.createMarketForwardRateColumn(false);
    td.addColumn(marketForwardRateCol);

    // Client All-In
    let clientForwardRateCol: BfxColDef = this.createClientForwardRateColumn();
    td.addColumn(clientForwardRateCol);

    // channel
    let channelCol: BfxColDef = this.createChannelColumn();
    td.addColumn(channelCol);

    // client name
    let clientNameCol: BfxColDef = this.createClientNameColumn();
    td.addColumn(clientNameCol);

    // client id
    let clientIdCol: BfxColDef = this.createClientIDColumn();
    td.addColumn(clientIdCol);

    // Client Comment
    let clientCommentCol: BfxColDef = this.createClientCommentColumn();
    td.addColumn(clientCommentCol);

    if (this.privateCommentsEnabled) {
      // Private Comment
      let privateCommentCol: BfxColDef = this.createPrivateCommentColumn();
      td.addColumn(privateCommentCol);
    }

    // Order id/Ref
    let orderIdCol: BfxColDef = this.createOrderIDColumn();
    td.addColumn(orderIdCol);

    // Allocation ID
    let allocationIdCol: BfxColDef = this.createAllocationIDColumn();
    td.addColumn(allocationIdCol);

    // entry user name
    let entryUserCol: BfxColDef = this.createEntryUserColumn();
    td.addColumn(entryUserCol);

    // Entry Date
    let entryDateCol: BfxColDef = this.createEntryDateColumn();
    td.addColumn(entryDateCol);

    if (this.enabledOBO) {
      // OBO Client
      let oboClientCol: BfxColDef = this.createOBOClientColumn();
      td.addColumn(oboClientCol);

      // OBO Account
      let oboAccountCol: BfxColDef = this.createOBOAccountColumn();
      td.addColumn(oboAccountCol);
    }

    // usd entered amount
    const usdEquivCol: BfxColDef = this.createUSDEquivColumn();
    td.addColumn(usdEquivCol);

    // salesGroup
    let salesGroupCol: BfxColDef = this.createSalesGroupColumn();
    td.addColumn(salesGroupCol);
    return td;
  }

  public override getBlotterType(): CubiqTS.BlotterType {
    return null;
  }

  protected override getProfileSettingKey(): string {
    return `${ProfileSettingKeys.userProfileFwdRollBlotters}Orders`;
  }

  public override getVersionIdentifier(): string {
    return FWD_ROLL_ORDERS_TABLE_DEFINITION_VER;
  }

  protected createClientIDColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "Client ID";
    col.field = "clientCode";
    col.indexType = IndexType.String;
    col.minWidth = this.oneWs;
    col.width = this.sevenWs;
    col.columnFilterType = ColumnFilterType.CLIENTID;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = true;
    }
    col.valueFormatter = (params) => {
      if (StaticDataPrivatizationType.hasPrivatization(params.data)) {
        let displayName: string;
        const client = this.clientLookup.getClient(params.value);
        displayName = client?.clientId;
        return displayName ?? "";
      }
      return params.value;
    };
    return col;
  }
  protected createClientNameColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "Client";
    col.field = "clientName";
    // Turn off sortable as thtis is derived field
    col.sortable = false;
    col.indexType = IndexType.String;
    col.minWidth = this.oneWs;
    col.width = this.sevenWs;
    col.columnFilterType = ColumnFilterType.CLIENT;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = true;
    }
    col.valueFormatter = (params) => {
      let displayName: string;
      const client = this.clientLookup.getClient(params.data?.clientCode);
      displayName = client?.clientName;
      return displayName ?? "";
    };
    return col;
  }

  protected createClientCommentColumn() {
    let col: BfxColDef = { suppressMenu: true };

    col.field = "clientComments";
    col.headerName = "Client Comments";
    col.minWidth = this.oneWs;
    col.width = this.nineWs;
    col.showTooltip = true;
    col.valueFormatter = this.formatComment.bind(this);
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = true;
    }
    return col;
  }

  protected createPrivateCommentColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.field = "privateComments";
    col.headerName = "Private Comments";
    col.minWidth = this.oneWs;
    col.width = this.nineWs;
    col.showTooltip = true;
    col.valueFormatter = this.formatComment.bind(this);
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = true;
    }
    return col;
  }

  protected formatComment(params: ValueFormatterParams): string {
    return params.value != null ? params.value : "-";
  }
}
